import React from 'react';
import { useGetPlansCultureActionsQuery, useDeletePlansCultureActionMutation } from '@/areas/culture-hub/api/plansCultureActions';
import { StyledAddButton } from '@/common/components/button/StyledButton';
import { useToasts } from 'common/toast';
import CultureActionModal from './CultureActionModal';
import { ChildTable as TableInner, ChildHeader as TableHeader, ChildHeaderCell as HeaderCell, ChildBody as TableBody, ChildRow as TableRow, ChildCell as TableCell } from '@/common/components/TableChild';
import { StyledTrashButton } from '@/common/components/button/StyledButton';
import FocusPlanDeleteConfirm from '../action-planner/FocusPlanDeleteConfirm';
import { ClipboardList } from 'lucide-react';
import { Caption, H2, PageSection } from '@/common/styles/page';

import { renderInvolvedIcon, getInvolvedLabel } from './involvedHelpers';

const IMPACT_LEVELS = [
    { value: 0, label: 'None', bg: 'bg-green-100' },
    { value: 1, label: 'Low', bg: 'bg-green-200' },
    { value: 2, label: 'Medium', bg: 'bg-green-300' },
    { value: 3, label: 'High', bg: 'bg-green-600' },
    { value: 4, label: 'Huge', bg: 'bg-green-800' },
];

// Hex colors used for icon fills to match modal green scale
const IMPACT_COLORS = ['#DCFCE7', '#86EFAC', '#4ADE80', '#16A34A', '#09815f'];

interface CultureActionsProps {
    actionPlanId?: number | null; // assessment period action plan id
}

export const CultureActions: React.FC<CultureActionsProps> = ({ actionPlanId }) => {
    const { data, isLoading } = useGetPlansCultureActionsQuery(
        { assessmentPeriodActionPlanId: actionPlanId as number },
        { skip: !actionPlanId }
    );
    const [deleteAction] = useDeletePlansCultureActionMutation();
    const { addToast } = useToasts();

    const [openModal, setOpenModal] = React.useState(false);
    const [editing, setEditing] = React.useState<any | null>(null);

    // Confirm-delete state (matches PlannerTable behavior)
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [pendingItem, setPendingItem] = React.useState<{ itemId: number; title?: string } | null>(null);
    const [pendingScrollY, setPendingScrollY] = React.useState<number | null>(null);

    const handleConfirmDelete = async () => {
        const scrollY = pendingScrollY;
        if (pendingItem) {
            try {
                await deleteAction({ id: pendingItem.itemId }).unwrap();
                addToast('Culture action deleted', { appearance: 'success', autoDismiss: true });
            } catch (err: any) {
                console.error('Failed to delete culture action:', err);
                addToast('Failed to delete culture action', { appearance: 'error', autoDismiss: true });
            }
        }

        setConfirmOpen(false);
        setPendingItem(null);
        setPendingScrollY(null);

        setTimeout(() => {
            if (scrollY !== null) window.scrollTo(0, scrollY);
        }, 0);
    };

    const handleCancelDelete = () => {
        setConfirmOpen(false);
        setPendingItem(null);
        setPendingScrollY(null);
    };

    const openForEdit = (item: any) => {
        setEditing(item);
        setOpenModal(true);
    };

    const openForCreate = () => {
        setEditing(null);
        setOpenModal(true);
    };

    return (
        <PageSection>
            <div className="flex items-center justify-between mb-0">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full shadow-sm" style={{ background: '#3b82f6' }}>
                        <ClipboardList className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <H2>Culture Actions</H2>
                        <Caption>Track and manage your culture-related actions and their impact.</Caption>
                    </div>
                </div>
                <div>
                    <StyledAddButton label="Add Entry" onClick={openForCreate} disabled={!actionPlanId} />
                </div>
            </div>

            {!actionPlanId ? (
                <div className="text-sm text-gray-500">No action plan selected. Create or select an action plan before adding culture actions.</div>
            ) : isLoading ? (
                <div className="text-sm text-gray-500">Loading entries...</div>
            ) : !data || data.length === 0 ? (
                <TableInner selectable>
                    <TableHeader>
                        <tr>
                            <HeaderCell>Action Taken</HeaderCell>
                            <HeaderCell>Observations</HeaderCell>
                            <HeaderCell>Impact</HeaderCell>
                            <HeaderCell>Next Actions</HeaderCell>
                            <HeaderCell justify="right">Action</HeaderCell>
                        </tr>
                    </TableHeader>
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={5} className="p-12 text-center text-gray-500">No culture action entries yet.</TableCell>
                        </TableRow>
                    </TableBody>
                </TableInner>
            ) : (
                <TableInner selectable>
                    <TableHeader>
                        <tr>
                            <HeaderCell width={4}>Action Taken</HeaderCell>
                            <HeaderCell width={4}>Observations</HeaderCell>
                            <HeaderCell width={3}>Impact</HeaderCell>
                            <HeaderCell width={4}>Next Actions</HeaderCell>
                            <HeaderCell width={1} justify="right">Action</HeaderCell>
                        </tr>
                    </TableHeader>
                    <TableBody>
                        {data.map((entry: any) => (
                            <TableRow
                                key={entry.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => openForEdit(entry)}
                                onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); openForEdit(entry); } }}
                            >
                                <TableCell $firstcol>
                                    <div className="min-w-0">
                                        <div className="text-sm break-text text-gray-500 line-clamp-4">{entry.action_taken || '—'}</div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="text-sm break-text text-gray-500 line-clamp-4">{entry.observations || '—'}</div>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-start">
                                        <div className="p-1 rounded-full hover:bg-gray-100 transition-colors" title={getInvolvedLabel(entry.involved_party)} aria-label={getInvolvedLabel(entry.involved_party)} aria-hidden>
                                            {(() => {
                                                if (typeof entry.impact !== 'undefined') {
                                                    const idx = Math.min(Math.max(Number(entry.impact) || 0, 0), 4);
                                                    const color = IMPACT_COLORS[idx];
                                                    const title = `${getInvolvedLabel(entry.involved_party)} — ${IMPACT_LEVELS[idx].label} impact`;
                                                    return renderInvolvedIcon(entry.involved_party, 18, color);
                                                }

                                                return renderInvolvedIcon(entry.involved_party, 18);
                                            })()}
                                        </div>

                                        <div className="ml-2 flex flex-col">
                                            <div className="text-sm text-gray-500">{getInvolvedLabel(entry.involved_party)}</div>
                                            {typeof entry.impact !== 'undefined' ? (() => {
                                                const idx = Math.min(Math.max(Number(entry.impact) || 0, 0), 4);
                                                const level = IMPACT_LEVELS[idx];
                                                return (
                                                    <div className="text-xs text-gray-500 mt-0.5">{`${level.label} impact`}</div>
                                                );
                                            })() : <div className="text-sm text-gray-500">—</div>}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <div className="text-sm break-text text-gray-500 line-clamp-4">{entry.next_actions || '—'}</div>
                                </TableCell>

                                <TableCell justify="right">
                                    <StyledTrashButton
                                        id={entry.id}
                                        onClick={(maybeEventOrId: any) => {
                                            if (maybeEventOrId && typeof maybeEventOrId.stopPropagation === 'function') {
                                                maybeEventOrId.stopPropagation();
                                            }
                                            setPendingItem({ itemId: entry.id, title: entry.action_taken || '—' });
                                            setPendingScrollY(window.scrollY);
                                            setConfirmOpen(true);
                                        }}
                                        title="Delete"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </TableInner>
            )}

            <FocusPlanDeleteConfirm
                open={confirmOpen}
                itemTitle={pendingItem?.title}
                onCancel={handleCancelDelete}
                onConfirm={handleConfirmDelete}
            />

            <CultureActionModal open={openModal} onClose={() => setOpenModal(false)} actionPlanId={actionPlanId ?? undefined} initial={editing} />
        </PageSection>
    );
};

export default CultureActions;
